'use client';

import Slider from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

import { carregarLogos } from '@/utils/carregar-logos';
import styles from '@/styles/highlight-jobs.module.css';
import { HighlightJob } from '@/types/job';
import React from 'react';
import HighlightJobCard from './highlight-job-card';
import { updateSlickSlideFocusability } from '@/utils/slick-a11y';

const SlickSlider = Slider as unknown as React.ComponentType<{
  children?: React.ReactNode;
  className?: string;
  [key: string]: unknown;
}>;

const settings = {
  centerMode: false,
  dots: false,
  infinite: false,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 2,
  rows: 2,
  slidesPerRow: 1,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
        rows: 2,
        slidesPerRow: 1,
        centerMode: false,
      },
    },
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
        rows: 1,
        slidesPerRow: 1,
        centerMode: false,
        initialSlide: 0,
      },
    },
    {
      breakpoint: 480,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        rows: 1,
        slidesPerRow: 1,
        centerMode: true,
        initialSlide: 0,
      },
    },
    {
      breakpoint: 320,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        rows: 1,
        slidesPerRow: 1,
        centerMode: false,
        initialSlide: 0,
      },
    },
  ],
};

interface HighlightJobsProps {
  jobs: HighlightJob[];
}

interface HighlightJobsState {
  jobsLogo: string[];
}

// 6 cards visiveis no desktop (slidesToShow 3 x rows 2), mais 2 de antecipacao.
// No mobile so 1 fica visivel, entao a janela sobra — mesmo assim sao 8
// requisicoes em vez de 30. Ver src/utils/carregar-logos.ts para os numeros.
const JANELA = 8;

export default class HighlightJobs extends React.Component<HighlightJobsProps, HighlightJobsState> {
  private sliderRootRef = React.createRef<HTMLDivElement>();

  constructor(props: any) {
    super(props);
    this.state = {
      jobsLogo: [],
    };
  }

  private logosPedidos = new Set<number>();
  private pedidosAte = 0;

  async componentDidMount(): Promise<void> {
    await this.carregarProximos();

    window.requestAnimationFrame(this.updateSlideFocusability);
  }

  // Avanca a janela a partir do que ja foi pedido, e nao do indice que o slick
  // informa: com `rows: 2` o indice do slide agrupa dois cards, entao contar
  // pelo indice do slide erraria quantos logos faltam.
  carregarProximos = async (): Promise<void> => {
    const ids = this.props.jobs.map((job) => job.id);
    this.pedidosAte = Math.min(this.pedidosAte + JANELA, ids.length);

    const parcial = await carregarLogos(ids, this.pedidosAte, this.logosPedidos);
    if (!parcial) return;

    this.setState((anterior) => {
      const jobsLogo = [...anterior.jobsLogo];
      for (const [indice, logo] of Object.entries(parcial)) {
        jobsLogo[Number(indice)] = logo;
      }
      return { jobsLogo };
    });
  };

  componentDidUpdate(): void {
    this.updateSlideFocusability();
  }

  updateSlideFocusability = () => {
    updateSlickSlideFocusability(this.sliderRootRef.current);
  };

  render(): React.ReactNode {
    const {
      props,
      state: { jobsLogo },
    } = this;
    return (
      <div className={styles.highlight}>
        <div className="container">
          <div className="row">
            <div className="col-12 d-flex flex-column align-items-center">
              <h2>Vagas em destaque</h2>
              {props.jobs?.length > 0 ? (
                <div ref={this.sliderRootRef} className={styles.highlight_list}>
                  <SlickSlider
                    {...settings}
                    className={styles.highlight_list}
                    onInit={this.updateSlideFocusability}
                    onReInit={this.updateSlideFocusability}
                    afterChange={this.updateSlideFocusability}
                    beforeChange={this.carregarProximos}
                  >
                    {props.jobs.map((job: any, index: number) => (
                      <HighlightJobCard key={index} data={{ ...job, companyLogo: jobsLogo[index] }} />
                    ))}
                  </SlickSlider>
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
